import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { ringBorder } from './portalkit/tokens';
import { safeBottom } from '../utils/safeArea';

/**
 * ProfileAnthem — the floating bottom-right player for a Portal Page owner's Spotify track
 * (design_handoff_portal_profile_editor, "Anthem player", Oct 2 2026).
 *
 * Collapsed: a 58px album-art puck, radius 18, a 2px ring (#1F1F1F; the party ring while
 * playing). Expanded: a 288px card — 52px art, ANTHEM / NOW PLAYING (green), name, artist, HIDE
 * and CLOSE, then PLAY · SPOTIFY pills with a preview or the Spotify embed without one.
 *
 * Autoplay: browsers block audible autoplay without a gesture, so with `autoplay` on and a
 * preview MP3 the track starts on the visitor's first pointer / key gesture (an immediate attempt
 * is made too, which is usually blocked, harmlessly). Without a preview the embed is tap-to-play.
 *
 * 16px off the corner; 84px up on a phone when the bottom nav is there (`chrome`). `openKey`
 * changing opens the card and starts the preview — the MUSIC section's ANTHEM row drives it.
 * `preview` (the editor's live preview) renders the puck with no audio and no listeners.
 *
 * @param {{ trackId, name, artist, albumArt, previewUrl, spotifyUrl, autoplay }} anthem
 */
const PREVIEW_VOLUME = 0.5;

export default function ProfileAnthem({ anthem, chrome = true, phone, openKey = 0, preview = false }) {
  const { isAuthenticated } = useAuth();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const hasPreview = !!anthem?.previewUrl;
  const lastOpen = useRef(openKey);
  const live = useRef(true);

  // Leaving the page must silence it. React removing the <audio> is not enough: WebKit keeps a
  // detached media element playing while anything references it (Chrome pauses it, per spec),
  // and the first-gesture autoplay below is often the very tap that navigates away — its play()
  // resolves after unmount, on an element nobody can reach, looping forever. So every play goes
  // through `start`, which re-checks `live` once the promise lands, and unmount pauses and drops
  // the source outright.
  // React nulls a plain ref before passive cleanups run, so the element is kept by hand.
  const holdAudio = useCallback((el) => { if (el) audioRef.current = el; }, []);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      const audio = audioRef.current;
      if (!audio) return;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    };
  }, []);
  const start = useCallback((audio) => audio.play().then(() => {
    if (!live.current) { audio.pause(); return; }
    setPlaying(true);
  }).catch(() => {}), []);

  // Half volume however it starts (autoplay, PLAY, the ANTHEM row): it is the page's backdrop.
  useEffect(() => { if (audioRef.current) audioRef.current.volume = PREVIEW_VOLUME; }, [hasPreview, preview, anthem]);

  useEffect(() => {
    if (preview || !anthem?.trackId || !hasPreview || !anthem.autoplay) return undefined;
    const audio = audioRef.current;
    if (!audio) return undefined;
    const tryPlay = () => start(audio);
    tryPlay();
    const events = ['pointerdown', 'keydown', 'touchstart'];
    const onFirst = () => { if (audio.paused) tryPlay(); cleanup(); };
    const cleanup = () => events.forEach((e) => window.removeEventListener(e, onFirst));
    events.forEach((e) => window.addEventListener(e, onFirst));
    return cleanup;
  }, [anthem, hasPreview, preview, start]);

  // An ANTHEM row tap on the page: open (even after CLOSE) and start the preview.
  useEffect(() => {
    if (openKey === lastOpen.current) return;
    lastOpen.current = openKey;
    setDismissed(false);
    setExpanded(true);
    if (!preview && hasPreview && audioRef.current?.paused) start(audioRef.current);
  }, [openKey, hasPreview, preview, start]);

  if (!anthem?.trackId || dismissed) return null;

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) start(audio);
    else { audio.pause(); setPlaying(false); }
  };
  const close = () => { if (audioRef.current) audioRef.current.pause(); setPlaying(false); setDismissed(true); };

  // Below lg the bottom nav floats 58px + 10px (+ the safe area) off the bottom, so the player
  // rides 84px up there; the nav is only there when signed in.
  const aboveNav = chrome && (phone ?? true) && isAuthenticated;
  const bottom = aboveNav ? safeBottom(84) : safeBottom(16);
  const label = { fontSize: 9, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase' };

  return (
    <div className="pk" style={{ position: 'fixed', right: 16, bottom, zIndex: 45 }} data-testid="profile-anthem" data-above-nav={aboveNav ? '1' : undefined}>
      {expanded ? (
        <div style={{ width: 288, maxWidth: 'calc(100vw - 32px)', display: 'flex', flexDirection: 'column', gap: 10, padding: 10, borderRadius: 24, background: '#111', color: '#fff', boxShadow: 'inset 0 0 0 1px #2A2A2A, 0 12px 40px rgba(0,0,0,.6)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {anthem.albumArt ? (
              <img src={anthem.albumArt} alt="" style={{ flexShrink: 0, width: 52, height: 52, borderRadius: 14, objectFit: 'cover', display: 'block' }} />
            ) : (
              <span aria-hidden="true" style={{ flexShrink: 0, width: 52, height: 52, borderRadius: 14, background: '#000' }} />
            )}
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ ...label, color: playing ? '#1DB954' : '#8A8A8A' }}>{playing ? 'NOW PLAYING' : 'ANTHEM'}</span>
              <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{anthem.name}</span>
              <span style={{ fontSize: 11, color: '#8A8A8A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{anthem.artist}</span>
            </span>
            <span style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <button type="button" onClick={() => setExpanded(false)} className="pk-btn pk-text-on-black" style={{ padding: '2px 4px', borderRadius: 4, ...label, fontSize: 9, letterSpacing: '.14em' }}>HIDE</button>
              <button type="button" onClick={close} className="pk-btn pk-text-on-black" style={{ padding: '2px 4px', borderRadius: 4, ...label, fontSize: 9, letterSpacing: '.14em' }}>CLOSE</button>
            </span>
          </div>
          {hasPreview ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={togglePlay} className="pk-btn pk-primary-on-black" style={{ flex: 1, height: 40, fontSize: 10, fontWeight: 800, letterSpacing: '.14em' }}>{playing ? 'PAUSE' : 'PLAY'}</button>
              {anthem.spotifyUrl && (
                <a href={anthem.spotifyUrl} target="_blank" rel="noreferrer" className="pk-btn" style={{ height: 40, padding: '0 16px', background: '#1F1F1F', color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: '.14em', textDecoration: 'none' }}>SPOTIFY</a>
              )}
            </div>
          ) : preview ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 64, padding: '0 14px', borderRadius: 16, background: '#000', boxShadow: 'inset 0 0 0 1px #2A2A2A' }}>
              <span style={{ width: 32, height: 32, borderRadius: '50%', background: '#1DB954', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="12" height="12" viewBox="0 0 10 10" fill="#000" aria-hidden="true"><path d="M2 1l7 4-7 4z" /></svg></span>
              <span style={{ fontSize: 10, lineHeight: 1.5, color: '#8A8A8A' }}>Spotify player · tap to play</span>
            </div>
          ) : (
            <iframe
              title={`${anthem.name} on Spotify`}
              src={`https://open.spotify.com/embed/track/${anthem.trackId}?theme=0`}
              width="100%"
              height="80"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              style={{ display: 'block', borderRadius: 16 }}
            />
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => { setExpanded(true); if (!preview && hasPreview && audioRef.current?.paused) togglePlay(); }}
          aria-label={`Play ${anthem.name}`}
          style={{ position: 'relative', display: 'block', width: 58, height: 58, padding: 0, border: '2px solid transparent', borderRadius: 18, overflow: 'hidden', background: playing ? ringBorder('#000') : '#1F1F1F', cursor: 'pointer' }}
        >
          {anthem.albumArt ? (
            <img src={anthem.albumArt} alt="" style={{ width: '100%', height: '100%', borderRadius: 15, objectFit: 'cover', display: 'block' }} />
          ) : (
            <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 15, background: '#000', color: '#fff', ...label }}>Anthem</span>
          )}
        </button>
      )}
      {hasPreview && !preview && <audio ref={holdAudio} src={anthem.previewUrl} loop preload="auto" />}
    </div>
  );
}
